/**
 * How the brief's text is read on the way into the box: its `{{details}}` and its markdown.
 *
 * THE DETAILS ARE THE WORKER'S VERDICT, MIRRORED. `segmentPlaceholders` uses the Worker's own
 * placeholder pattern from `validateInstructions`, so a detail painted purple is one the
 * Worker will fill in, and one painted red is one it will refuse on save.
 *
 * THE MARKDOWN IS A SMALL, LINE-BY-LINE READING, not a parser. The text reaches the model
 * exactly as it was typed and the model reads markdown on its own; this only decides what to
 * paint. So it knows the handful an owner types in a brief - headings, lists, bold, italic,
 * code - and anything it does not know stays plain, which costs nothing.
 *
 * A DETAIL IS ONE TOKEN TO THE MARKDOWN. The details are masked out before the markdown is
 * read, so `{{business_name}}` is never half-italic from its underscore, and a detail inside
 * a heading or bold text keeps its colour as well as the heading's or the bold's weight.
 *
 * SO IS A TOOL, NAMED `@book_appointment`. Nothing renders it: the model reads the text as
 * typed and knows its tools by these names, so the `@` only marks the name for the box -
 * painted as the tool, or red when no tool has that name (renamed, deleted, or a handle
 * that was never one). Unlike a detail the Worker does not refuse it; to the model it is words.
 *
 * THE EDITS ARE SPLICES, one range replaced with one string, so the box can apply them as
 * typing and keep the platform's undo.
 */

/** The Worker's pattern: `{{ name }}` with any spacing inside the braces. */
const PLACEHOLDER = /(\{\{\s*[^}]*?\s*\}\})/;

export interface Segment {
  text: string;
  kind: "plain" | "known" | "unknown";
}

/** The text cut at its placeholders, each marked known or unknown, and the unknown names. */
export function segmentPlaceholders(text: string, known: readonly string[]): { segments: Segment[]; unknown: string[] } {
  const unknown: string[] = [];
  const segments = text
    .split(PLACEHOLDER)
    .filter((part) => part !== "")
    .map((part): Segment => {
      const match = part.match(/^\{\{\s*([^}]*?)\s*\}\}$/);
      if (!match) return { text: part, kind: "plain" };
      const name = match[1]!;
      if (known.includes(name)) return { text: part, kind: "known" };
      if (!unknown.includes(name)) unknown.push(name);
      return { text: part, kind: "unknown" };
    });
  return { segments, unknown };
}

interface Style {
  /** Markdown's own characters - `##`, `**`, `- ` - which stay visible but step back. */
  marker?: boolean;
  heading?: boolean;
  strong?: boolean;
  em?: boolean;
  code?: boolean;
}

export interface BriefSegment extends Style {
  text: string;
  detail?: "known" | "unknown";
  tool?: ToolState;
}

/** What the box needs to know of a tool: its name, and whether it is on. */
export interface ToolRef {
  name: string;
  enabled: boolean;
}

export type ToolState = "on" | "off" | "unknown";

/**
 * `@name` with the Worker's tool-name rule. Not after a word character, a dot or another `@`,
 * so `info@acme.com` is an address and not a tool; not followed by one either, so a name
 * with capitals or past the 64-character limit is not half of one.
 */
const TOOL_MENTION = /(?<![\w.@])@([a-z][a-z0-9_]{1,63})(?!\w)/g;

export interface Mention {
  start: number;
  end: number;
  name: string;
  state: ToolState;
}

/** Every `@tool` in the text, with whether that tool exists and is on. */
export function toolMentions(text: string, tools: readonly ToolRef[]): Mention[] {
  return [...text.matchAll(TOOL_MENTION)].map((match) => {
    const name = match[1]!;
    const found = tools.find((tool) => tool.name === name);
    return { start: match.index, end: match.index + match[0].length, name, state: found ? (found.enabled ? "on" : "off") : "unknown" };
  });
}

const HEADING = /^#{1,6} /;
const LIST = /^[ \t]*(?:[-*+]|\d+[.)]) /;
/** Code, then bold, then italic in either spelling; each inside one line. */
const INLINE =
  /(`)([^`\n]+)`|(\*\*)([^\s*](?:[^\n]*?[^\s*])?)\*\*|(\*)([^\s*](?:[^*\n]*?[^\s*])?)\*|(?<![\p{L}\p{N}_])(_)([^\s_](?:[^_\n]*?[^\s_])?)_(?![\p{L}\p{N}_])/gu;

/** The text cut into runs that each paint one way. Joined back, the runs are the text. */
export function segmentBrief(text: string, known: readonly string[], tools: readonly ToolRef[] = []): BriefSegment[] {
  const tokens: Array<{ start: number; end: number; mark: Pick<BriefSegment, "detail" | "tool"> }> = [];
  let at = 0;
  for (const segment of segmentPlaceholders(text, known).segments) {
    if (segment.kind !== "plain") tokens.push({ start: at, end: at + segment.text.length, mark: { detail: segment.kind } });
    at += segment.text.length;
  }
  // An `@` inside a detail's braces is part of that detail.
  const inDetail = (pos: number) => tokens.some((token) => pos >= token.start && pos < token.end);
  for (const mention of toolMentions(text, tools)) {
    if (!inDetail(mention.start)) tokens.push({ start: mention.start, end: mention.end, mark: { tool: mention.state } });
  }
  tokens.sort((a, b) => a.start - b.start);
  // A private-use character is neither a space nor a marker, so a masked token reads as a word.
  let masked = text;
  for (const { start, end } of tokens) masked = masked.slice(0, start) + "".repeat(end - start) + masked.slice(end);

  const runs: Array<{ start: number; end: number; style: Style }> = [];
  const push = (start: number, end: number, style: Style) => {
    if (end > start) runs.push({ start, end, style });
  };

  function inline(from: number, to: number, style: Style) {
    let cursor = from;
    for (const match of masked.slice(from, to).matchAll(INLINE)) {
      const start = from + match.index;
      const [mark, inner, flag] = match[1]
        ? [match[1], match[2]!, "code" as const]
        : match[3]
          ? [match[3], match[4]!, "strong" as const]
          : match[5]
            ? [match[5], match[6]!, "em" as const]
            : [match[7]!, match[8]!, "em" as const];
      const open = start + mark.length;
      const close = open + inner.length;
      push(cursor, start, style);
      push(start, open, { ...style, marker: true });
      if (flag === "code") push(open, close, { ...style, code: true });
      else inline(open, close, { ...style, [flag]: true });
      push(close, close + mark.length, { ...style, marker: true });
      cursor = close + mark.length;
    }
    push(cursor, to, style);
  }

  let lineStart = 0;
  for (const line of masked.split("\n")) {
    if (lineStart > 0) push(lineStart - 1, lineStart, {});
    const heading = line.match(HEADING)?.[0];
    const list = heading ? undefined : line.match(LIST)?.[0];
    const prefix = heading ?? list ?? "";
    push(lineStart, lineStart + prefix.length, { marker: true });
    inline(lineStart + prefix.length, lineStart + line.length, heading ? { heading: true } : {});
    lineStart += line.length + 1;
  }

  // Cut each run again wherever a token starts or ends, and give the pieces inside it its colour.
  const segments: BriefSegment[] = [];
  for (const run of runs) {
    let cursor = run.start;
    for (const token of tokens) {
      if (token.end <= cursor || token.start >= run.end) continue;
      const start = Math.max(token.start, cursor);
      const end = Math.min(token.end, run.end);
      if (start > cursor) segments.push({ text: text.slice(cursor, start), ...run.style });
      segments.push({ text: text.slice(start, end), ...run.style, ...token.mark });
      cursor = end;
    }
    if (run.end > cursor) segments.push({ text: text.slice(cursor, run.end), ...run.style });
  }
  return segments;
}

/** Replace `start..end` with `insert`, then select `selStart..selEnd`. */
export interface Edit {
  start: number;
  end: number;
  insert: string;
  selStart: number;
  selEnd: number;
}

export function applyEdit(text: string, edit: Edit): string {
  return text.slice(0, edit.start) + edit.insert + text.slice(edit.end);
}

/**
 * Bold or italic on the selection, or off again if it already has it. Spaces at either end of
 * the selection stay outside the markers, because `**word **` is not bold in markdown.
 */
export function wrapSelection(text: string, start: number, end: number, mark: "**" | "*"): Edit {
  while (end > start && /\s/.test(text[end - 1]!)) end--;
  while (start < end && /\s/.test(text[start]!)) start++;
  const selected = text.slice(start, end);
  const n = mark.length;
  // An italic star that is really half of a bold pair is not italic.
  const isMark = (at: number) =>
    text.slice(at, at + n) === mark && (mark === "**" || text[at - 1] !== "*" || text.slice(at - 3, at) === "**");
  const outside = start >= n && isMark(start - n) && text.slice(end, end + n) === mark && (mark === "**" || text[end + n] !== "*");
  if (outside) return { start: start - n, end: end + n, insert: selected, selStart: start - n, selEnd: end - n };
  const inside = selected.length >= 2 * n && selected.startsWith(mark) && selected.endsWith(mark) && (mark === "**" || !selected.startsWith("**"));
  if (inside) {
    const inner = selected.slice(n, -n);
    return { start, end, insert: inner, selStart: start, selEnd: start + inner.length };
  }
  return { start, end, insert: mark + selected + mark, selStart: start + n, selEnd: end + n };
}

/**
 * A heading or a list on every line the selection touches, or off again if they all have it.
 * A heading replaces another level of heading rather than stacking on it.
 */
export function toggleLinePrefix(text: string, start: number, end: number, prefix: "## " | "- "): Edit {
  const from = text.lastIndexOf("\n", start - 1) + 1;
  const stop = end > start && text[end - 1] === "\n" ? end - 1 : end;
  const next = text.indexOf("\n", stop);
  const to = next === -1 ? text.length : next;
  const lines = text.slice(from, to).split("\n");
  const has = prefix === "## " ? /^## / : /^[ \t]*[-*+] /;
  const written = lines.filter((line) => line.trim() !== "");
  const off = written.length > 0 && written.every((line) => has.test(line));
  const changed = lines.map((line) => {
    if (off) return line.replace(has, (found) => found.replace(/[-*+#]+ $/, ""));
    if (line.trim() === "" && lines.length > 1) return line;
    if (prefix === "## ") return prefix + line.replace(HEADING, "");
    // A numbered item becomes a bullet; any other line takes one after its indent.
    return /^[ \t]*\d+[.)] /.test(line) ? line.replace(/^([ \t]*)\d+[.)] /, "$1- ") : line.replace(/^[ \t]*/, "$&- ");
  });
  const insert = changed.join("\n");
  if (start === end) {
    const caret = Math.max(from, start + (changed[0]!.length - lines[0]!.length));
    return { start: from, end: to, insert, selStart: caret, selEnd: caret };
  }
  return { start: from, end: to, insert, selStart: from, selEnd: from + insert.length };
}

/**
 * Enter on a list item: the next item's marker, or - on an item left empty - the end of the
 * list, the way every editor with lists does it. Null when the line is not a list item.
 */
export function continueList(text: string, caret: number): Edit | null {
  const from = text.lastIndexOf("\n", caret - 1) + 1;
  const next = text.indexOf("\n", caret);
  const to = next === -1 ? text.length : next;
  const line = text.slice(from, to);
  const match = line.match(/^([ \t]*)(?:([-*+])|(\d+)([.)])) /);
  if (!match || caret < from + match[0].length) return null;
  if (line.slice(match[0].length).trim() === "") return { start: from, end: to, insert: "", selStart: from, selEnd: from };
  const marker = match[2] ?? `${Number(match[3]) + 1}${match[4]}`;
  const insert = `\n${match[1]}${marker} `;
  return { start: caret, end: caret, insert, selStart: caret + insert.length, selEnd: caret + insert.length };
}

/**
 * An unfinished `{{` just before the caret, and what has been typed after it - which is what
 * opens the details menu in the box and filters it. Null once the braces are closed, or once
 * anything that cannot be in a detail's name has been typed after them.
 */
export function detailTrigger(text: string, caret: number): { start: number; query: string } | null {
  const match = text.slice(0, caret).match(/\{\{\s*([A-Za-z_]*)$/);
  return match ? { start: match.index!, query: match[1]! } : null;
}

/** An `@` just before the caret and the name typed after it: what opens the tools menu. */
export function toolTrigger(text: string, caret: number): { start: number; query: string } | null {
  const match = text.slice(0, caret).match(/(?<![\w.@])@([a-z0-9_]*)$/);
  return match ? { start: match.index!, query: match[1]! } : null;
}

export type Reference =
  | { kind: "detail"; from: number; to: number; key: string }
  | { kind: "tool"; from: number; to: number; name: string; state: ToolState };

/**
 * The detail or the tool under `pos`, for the hover. A detail left open across lines is not
 * one - the strip under the box already names it.
 */
export function referenceAt(text: string, pos: number, tools: readonly ToolRef[]): Reference | null {
  const lineStart = text.lastIndexOf("\n", pos - 1) + 1;
  const next = text.indexOf("\n", pos);
  const line = text.slice(lineStart, next === -1 ? text.length : next);
  for (const match of line.matchAll(/\{\{\s*([^}\n]*?)\s*\}\}/g)) {
    const from = lineStart + match.index;
    const to = from + match[0].length;
    if (pos >= from && pos <= to) return { kind: "detail", from, to, key: match[1]! };
  }
  for (const mention of toolMentions(line, tools)) {
    const from = lineStart + mention.start;
    const to = lineStart + mention.end;
    if (pos >= from && pos <= to) return { kind: "tool", from, to, name: mention.name, state: mention.state };
  }
  return null;
}
